import { useAuth } from '@clerk/clerk-react'
import { useMemo } from 'react'

const PETRI_BASE_URL = 'https://petri.snootsvet.com'
const PETRI_ENVIRONMENT =
  import.meta.env.VITE_ENVIRONMENT === 'production' ? 'production' : 'development'

export type PetriRating = 'pass' | 'fail'

/** A review as returned by Petri. */
export type PetriReview = {
  staffName: string
  rating: PetriRating
  comment: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Request body for creating or updating a review. The reviewer is derived from the
 * Clerk session on the server.
 */
export type PostPetriReview = {
  externalId: string
  rating: PetriRating
  comment?: string
}

/** Petri returned 404 because the log does not exist for this external ID and environment. */
export class PetriLogNotFoundError extends Error {
  constructor() {
    super('Petri log not found')
    this.name = 'PetriLogNotFoundError'
  }
}

async function errorMessage(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => null)
  return typeof body?.error === 'string' ? body.error : `${fallback} (${response.status})`
}

/** Direct client for Petri's review API. Injects the current Clerk session token. */
export function usePetriApi() {
  const { getToken } = useAuth()

  return useMemo(() => {
    async function headers(): Promise<Record<string, string>> {
      const token = await getToken()
      if (!token) throw new Error('Not signed in')
      return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }

    return {
      /**
       * Returns the current staff member's review, or null when the log exists but
       * has no review. Throws {@link PetriLogNotFoundError} when the log is missing.
       */
      async getReview(externalId: string): Promise<PetriReview | null> {
        const query = new URLSearchParams({
          externalId,
          environment: PETRI_ENVIRONMENT,
        })
        const response = await fetch(`${PETRI_BASE_URL}/api/public/reviews?${query}`, {
          headers: await headers(),
        })
        if (response.status === 404) throw new PetriLogNotFoundError()
        if (!response.ok) throw new Error(await errorMessage(response, 'Failed to load review'))

        const body = (await response.json()) as { review: PetriReview | null }
        return body.review
      },

      /** Creates or replaces the current staff member's review for a log. */
      async postReview(review: PostPetriReview): Promise<void> {
        const response = await fetch(`${PETRI_BASE_URL}/api/public/review`, {
          method: 'POST',
          headers: await headers(),
          body: JSON.stringify({ ...review, environment: PETRI_ENVIRONMENT }),
        })
        if (response.status === 404) throw new PetriLogNotFoundError()
        if (!response.ok) throw new Error(await errorMessage(response, 'Failed to send review'))
      },
    }
  }, [getToken])
}
